import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@/utils';

const Drawer = (props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root data-slot="drawer" {...props} />
);

function DrawerTrigger({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Trigger> & { ref?: React.Ref<HTMLButtonElement> }) {
  return <DrawerPrimitive.Trigger ref={ref} data-slot="drawer-trigger" {...props} />;
}

const DrawerPortal = (props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Portal>) => {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
};

function DrawerClose({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Close> & { ref?: React.Ref<HTMLButtonElement> }) {
  return <DrawerPrimitive.Close ref={ref} data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      data-slot="drawer-overlay"
      className={cn(
        `fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
        className,
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}) {
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        data-slot="drawer-content"
        className={cn(
          'group/drawer-content fixed z-50 flex h-auto flex-col bg-white',
          `data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24
          data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg
          data-[vaul-drawer-direction=top]:border-b`,
          `data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0
          data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[calc(100vh-3rem)]
          data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t`,
          `data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0
          data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l
          data-[vaul-drawer-direction=right]:sm:max-w-sm`,
          `data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0
          data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r
          data-[vaul-drawer-direction=left]:sm:max-w-sm`,
          className,
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          queueMicrotask(() => titleRef.current?.focus());
        }}
        {...props}
      >
        <div
          className="mx-auto mt-4 hidden h-1 w-[100px] shrink-0 rounded-full bg-muted
            group-data-[vaul-drawer-direction=bottom]/drawer-content:block"
        />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        `flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center
        group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left`,
        className,
      )}
      {...props}
    />
  );
}

function DrawerFilter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="drawer-filter" className={cn('p-4', className)} {...props} />;
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn('mt-auto flex justify-between gap-2 border-t border-[#ebebeb] bg-[#f8f8f8] p-4', className)}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & { ref?: React.Ref<HTMLHeadingElement> }) {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      data-slot="drawer-title"
      className={cn('font-semibold text-foreground', className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & { ref?: React.Ref<HTMLParagraphElement> }) {
  return (
    <DrawerPrimitive.Description
      ref={ref}
      data-slot="drawer-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFilter,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
